import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== ICONS ====================
const VideoCameraIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
  </svg>
);

const NewspaperIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
  </svg>
);

const UsersIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
  </svg>
);

const PlayCircleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z" />
  </svg>
);

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {eyebrow && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className={`mt-4 text-base md:text-lg text-gray-600 leading-7 ${align === "center" ? "mx-auto" : ""}`}>
        {description}
      </p>
    )}
  </div>
);

const FeatureCard = ({ icon, title, description }) => (
  <div className="group flex flex-col p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-school-blue/8 text-school-blue group-hover:bg-school-blue group-hover:text-white transition-colors duration-300 mb-6 shadow-sm">
      {icon}
    </div>
    <h3 className="text-lg md:text-xl font-heading font-bold text-gray-900 mb-2 tracking-tight group-hover:text-school-gold transition-colors">
      {title}
    </h3>
    <p className="text-sm md:text-base text-gray-600 leading-7">{description}</p>
  </div>
);

// ==================== MAIN LTG PHASE 2 COMPONENT ====================

const LTGPhase2 = () => {
  return (
    <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* ==================== HERO SECTION ==================== */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

          <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl bg-[#0F2240]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
                <span className="w-2 h-2 rounded-full bg-purple-400" aria-hidden="true" />
                LTG Phase 2
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
                LTG @ P.S. 85 Phase 2
              </h1>

              <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
                Student-Created News & Video Productions
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== FEATURED IMAGE ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <div className="relative max-w-5xl mx-auto">
              <div className="absolute -inset-4 bg-brand-50 rounded-[2.25rem] -z-10 transform translate-x-4 translate-y-4" aria-hidden="true" />
              <img
                src="https://3.files.edl.io/731c/23/04/28/111429-228e55db-a171-49cd-8ad4-bb0377925d9e.png"
                alt="PS 85 News - Participating Classes"
                className="rounded-[2.25rem] shadow-soft w-full border border-gray-100"
                loading="lazy"
              />
            </div>
          </Container>
        </section>

        {/* ==================== INTRODUCTION ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Phase 2"
              title="Participating Classes"
              description="Students become content creators, producers, and storytellers"
            />

            <div className="mt-10 max-w-4xl mx-auto">
              <p className="text-base md:text-lg text-gray-700 leading-relaxed text-center">
                In Phase 2 of our Learning Technology Grant, students became content creators, producers, and storytellers. Classes collaborated to create news segments, video productions, and multimedia presentations that showcase their learning and creativity.
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== FEATURES GRID ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Key Features"
              title="What Students Learn"
              description="Developing essential media production and storytelling skills"
            />

            <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
              <FeatureCard
                icon={<VideoCameraIcon />}
                title="Video Production"
                description="Students learn video filming, editing, and production techniques to create professional content."
              />

              <FeatureCard
                icon={<NewspaperIcon />}
                title="News Broadcasting"
                description="Classes create news segments covering school events, student achievements, and community stories."
              />

              <FeatureCard
                icon={<UsersIcon />}
                title="Collaboration"
                description="Teams work together on multimedia projects, developing teamwork and communication skills."
              />
            </div>
          </Container>
        </section>

        {/* ==================== CLASS PROJECTS ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Class Projects"
              title="Explore Student Work"
              description="Click any project to visit the class website and see what our students created."
            />

            {/* Collaboration */}
            <div className="mt-16">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-10 w-10 rounded-xl bg-school-blue flex items-center justify-center shrink-0">
                  <UsersIcon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-school-blue">Collaboration</h3>
                <div className="flex-1 h-px bg-school-blue/20 ml-2" aria-hidden="true" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  { name: 'Ms. Lewin', href: 'https://sites.google.com/schools.nyc.gov/ms-lewins-community-project/home' },
                  { name: 'Ms. Queenan', href: 'https://sites.google.com/schools.nyc.gov/308ltg?usp=sharing' },
                  { name: 'Ms. Fiscela', href: 'https://sites.google.com/schools.nyc.gov/25-26-308-ltg/home' },
                  { name: 'Ms. Krenn', href: 'https://sites.google.com/schools.nyc.gov/class420and419math/home' },
                  { name: 'Ms. Modesto', href: 'https://sites.google.com/schools.nyc.gov/community-economy85/videos-by-topic' },
                ].map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col items-center gap-4 p-7 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md hover:border-school-blue/20 hover:-translate-y-0.5 transition-all duration-300 text-center focus:outline-none focus:ring-2 focus:ring-school-blue/40"
                  >
                    <div className="h-14 w-14 rounded-2xl bg-school-blue/8 flex items-center justify-center group-hover:bg-school-blue group-hover:text-white text-school-blue transition-colors duration-300">
                      <UsersIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-gray-900 group-hover:text-school-gold transition-colors">{item.name}</p>
                      <p className="text-xs text-gray-500 mt-1">Collaboration Project</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-school-blue group-hover:text-school-gold transition-colors">
                      Visit Site
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                      </svg>
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* Video Production */}
            <div className="mt-14">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-10 w-10 rounded-xl bg-purple-600 flex items-center justify-center shrink-0">
                  <VideoCameraIcon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-school-blue">Video Production</h3>
                <div className="flex-1 h-px bg-school-blue/20 ml-2" aria-hidden="true" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5 max-w-2xl">
                {[
                  { name: 'Ms. Modesto', href: 'https://sites.google.com/schools.nyc.gov/communityeconomy/home' },
                  { name: '4th Grade Community Interview', href: 'https://sites.google.com/schools.nyc.gov/4th-gradeps85ltg/home' },
                ].map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col items-center gap-4 p-7 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md hover:border-purple-200 hover:-translate-y-0.5 transition-all duration-300 text-center focus:outline-none focus:ring-2 focus:ring-purple-400/40"
                  >
                    <div className="h-14 w-14 rounded-2xl bg-purple-50 flex items-center justify-center text-purple-600 group-hover:bg-purple-600 group-hover:text-white transition-colors duration-300">
                      <VideoCameraIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-gray-900 group-hover:text-purple-700 transition-colors">{item.name}</p>
                      <p className="text-xs text-gray-500 mt-1">Video Production Project</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-purple-600 group-hover:text-purple-800 transition-colors">
                      Visit Site
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                      </svg>
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* Phase 1 */}
            <div className="mt-14">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-10 w-10 rounded-xl bg-school-gold flex items-center justify-center shrink-0">
                  <NewspaperIcon className="w-5 h-5 text-school-blue" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-school-blue">Phase 1</h3>
                <div className="flex-1 h-px bg-school-blue/20 ml-2" aria-hidden="true" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5 max-w-sm">
                {[
                  { name: 'Mr. Hurd', href: 'https://sites.google.com/schools.nyc.gov/mr-hurds-community-project-202/home' },
                ].map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col items-center gap-4 p-7 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md hover:border-school-gold/30 hover:-translate-y-0.5 transition-all duration-300 text-center focus:outline-none focus:ring-2 focus:ring-school-gold/40"
                  >
                    <div className="h-14 w-14 rounded-2xl bg-school-gold/10 flex items-center justify-center text-school-gold group-hover:bg-school-gold group-hover:text-school-blue transition-colors duration-300">
                      <NewspaperIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-gray-900 group-hover:text-school-goldDark transition-colors">{item.name}</p>
                      <p className="text-xs text-gray-500 mt-1">Phase 1 Project</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-school-goldDark group-hover:text-school-blue transition-colors">
                      Visit Site
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                      </svg>
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* ==================== ENJOY OUR VIDEOS ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 max-w-5xl mx-auto">
              {/* QR code */}
              <div className="shrink-0 flex flex-col items-center gap-5">
                <div className="p-4 bg-white rounded-3xl border-2 border-gray-100 shadow-lg">
                  <img
                    src="/assets/images/qrcode.png"
                    alt="QR code for PS 85 YouTube Channel"
                    className="w-52 h-52 object-contain"
                  />
                </div>
                <p className="text-xs text-gray-500 font-medium tracking-wide uppercase text-center">
                  Scan to visit our channel
                </p>
              </div>

              {/* Text */}
              <div>
                <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs mb-4">
                  <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
                  YouTube Channel
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight mb-6">
                  Enjoy Our Videos!
                </h2>
                <p className="text-base md:text-lg text-gray-600 leading-7 mb-8">
                  Watch the incredible work our students have created — from news broadcasts to creative video projects that showcase their learning and talents. Scan the QR code or visit our YouTube channel to see it all.
                </p>
                <div className="inline-flex items-center gap-2 rounded-full bg-school-blue px-7 py-3.5 text-sm font-bold text-white shadow-soft">
                  <PlayCircleIcon />
                  PS 85 YouTube Channel
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* ==================== CTA SECTION ==================== */}
        <section className="relative py-24 md:py-32 bg-school-gold overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-school-blue/8 rounded-full blur-3xl pointer-events-none" />

          <Container className="relative text-center">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-school-blue mb-6">
              Explore More LTG Phases
            </h2>
            <p className="text-xl text-school-blue max-w-2xl mx-auto mb-10 font-light">
              Discover how our Learning Technology Grant continues to transform student learning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/ltg-phase-3"
                className="px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft"
              >
                View Phase 3
              </a>
              <a
                href="/ltg-phase-1"
                className="px-8 py-4 rounded-full bg-white/30 text-school-blue border-2 border-school-blue/20 font-medium hover:bg-white/50 transition-colors"
              >
                Back to Phase 1
              </a>
            </div>
          </Container>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default LTGPhase2;
